// forestshell: the decoder of the forest shell FSH1 (pipeline/forestshell.py,
// APP_FORESTVOL §2b, PROTOCOL.md "Forest shell") — one level (8 / 16 / 32 /
// 64 m) of one 1024 m tile: the conservative max-abs envelope over the
// crowns as a watertight lid + walls, the bound the far canopy volume marches
// in (gs-forestvol). Pure (no three.js). An unknown magic, version or a
// malformed body THROWS (no per-revision fallback).
//
// header 32 B LE: 'FSH1' · ver u8 · cell_m u8 · 2 B 0 · tile_m u16 · i i16 ·
// j i16 · y0 i16 (m) · nv u32 · nt u32 · nt_top u32 · 4 B 0. Body: x i16[nv],
// z i16[nv] (1/16 m from the tile's NW corner), y u16[nv] (1/32 m above y0),
// flag u8[nv] (0 lid, 1 wall top, 2 wall foot), a pad byte when nv is odd,
// u16[3 nt] indices (nt_top lid triangles first, then the walls; all face out).
export const FSH_MAGIC = 'FSH1';
export const FSH_VER = 1;
export const FSH_HEADER = 32;
export const FSH_XZ_Q = 16;               // x / z units per m
export const FSH_Y_Q = 32;                // y units per m
export const FSH_LEVELS = [8, 16, 32, 64];
export const FSH_FLAG = { lid: 0, wallTop: 1, wallFoot: 2 };

// The header alone -> {ver, cellM, tileM, i, j, y0, nv, nt, ntTop}; throws on
// a foreign magic / version / level.
export function readShellHeader(buf) {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  if (u8.length < FSH_HEADER) throw new Error(`forestshell: ${u8.length} B is shorter than the header`);
  const magic = String.fromCharCode(u8[0], u8[1], u8[2], u8[3]);
  if (magic !== FSH_MAGIC) throw new Error(`forestshell: magic ${JSON.stringify(magic)} is not ${FSH_MAGIC}`);
  const dv = new DataView(u8.buffer, u8.byteOffset, FSH_HEADER);
  const ver = dv.getUint8(4), cellM = dv.getUint8(5);
  if (ver !== FSH_VER) throw new Error(`forestshell: version ${ver} (this client reads ${FSH_VER})`);
  if (!FSH_LEVELS.includes(cellM)) throw new Error(`forestshell: unknown level ${cellM} m`);
  return { ver, cellM, tileM: dv.getUint16(8, true), i: dv.getInt16(10, true), j: dv.getInt16(12, true),
    y0: dv.getInt16(14, true), nv: dv.getUint32(16, true), nt: dv.getUint32(20, true), ntTop: dv.getUint32(24, true) };
}

// One file -> header fields + x / y / z (Float32Array, game metres), flag
// (Uint8Array), index (Uint16Array, 3 per triangle: the lid's ntTop first).
export function decodeShell(buf) {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  const hd = readShellHeader(u8);
  const { nv, nt, ntTop, tileM, i, j, y0 } = hd;
  const want = FSH_HEADER + 7 * nv + (nv % 2) + 6 * nt;
  if (u8.length !== want || ntTop > nt) throw new Error(`forestshell: ${u8.length} B, expected ${want} (nv ${nv}, nt ${nt}, nt_top ${ntTop})`);
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.length);
  const x = new Float32Array(nv), y = new Float32Array(nv), z = new Float32Array(nv);
  const ox = i * tileM, oz = j * tileM;
  let p = FSH_HEADER;
  for (let k = 0; k < nv; k++) x[k] = ox + dv.getInt16(p + 2 * k, true) / FSH_XZ_Q;
  p += 2 * nv;
  for (let k = 0; k < nv; k++) z[k] = oz + dv.getInt16(p + 2 * k, true) / FSH_XZ_Q;
  p += 2 * nv;
  for (let k = 0; k < nv; k++) y[k] = y0 + dv.getUint16(p + 2 * k, true) / FSH_Y_Q;
  p += 2 * nv;
  const flag = u8.slice(p, p + nv);
  p += nv + (nv % 2);
  const index = new Uint16Array(3 * nt);
  for (let k = 0; k < 3 * nt; k++) {
    const v = dv.getUint16(p + 2 * k, true);
    if (v >= nv) throw new Error(`forestshell: index ${v} past ${nv} vertices`);
    index[k] = v;
  }
  for (let k = 0; k < nv; k++) if (flag[k] > 2) throw new Error(`forestshell: vertex flag ${flag[k]}`);
  return { ...hd, x, y, z, flag, index };
}
